// shared-lib/dimensioning/boundaryPair.js
//
// Choosing the two boundaries a subject is centred BETWEEN, when the user has
// not named them.
//
// §3.2 makes the pair explicit because an L-shaped room has more than one wall
// facing an axis and "centred in the room" has no single answer there. That
// argument does not mean the user must always click twice: in a rectangular
// room there is exactly one candidate per side, and the obvious pick is the
// right one. This is that pick, and it degrades honestly — in an L-shaped room
// it takes the walls the subject actually FACES, which is a real answer, and
// the user can restate it against the other pair.
//
// Answers open question §8.3: the tie-break is NEAREST FACING wall per side.

const { segmentDir, dot2, perp } = require('./datums');

// Same threshold datums.js uses to call a wall "crossing" an axis.
const CROSSING_DOT = 0.2;

const AXES = { x: [1, 0], z: [0, 1] };

/**
 * The nearest wall on each side of `centre` along `u`, as `[loRef, hiRef]`.
 * PURE. Returns null when either side has no crossing wall — an open side has
 * nothing to be centred against, and inventing one would dimension to a
 * boundary that is not there.
 *
 * @param {Array} wallSegments  `{ wallId, start:[x,z], end:[x,z] }[]`
 * @param {[number,number]} centre  the subject's centre.
 * @param {[number,number]} u  unit axis.
 */
function nearestFacingPair(wallSegments, centre, u) {
  if (!Array.isArray(wallSegments) || !centre || !u) return null;
  const s0 = dot2(centre, u);

  // The dimension runs at the subject's own cross-axis position, so a wall
  // only bounds it if it actually SPANS that position. Without this check an
  // L-shaped room picks the nearer wall of the OTHER leg — one the subject is
  // not standing in front of at all, and one the dimension line would never
  // reach.
  const v = perp(u);
  const c0 = dot2(centre, v);

  let below = null;
  let above = null;
  for (const seg of wallSegments) {
    const dir = segmentDir(seg);
    const id = seg?.wallId || seg?.id;
    if (!dir || !id) continue;
    // Only a wall that CROSSES the axis bounds it; one running along the axis
    // is beside the subject, not in front of it.
    if (Math.abs(dot2(dir, u)) >= CROSSING_DOT) continue;

    const cA = dot2(seg.start, v);
    const cB = dot2(seg.end, v);
    if (c0 < Math.min(cA, cB) || c0 > Math.max(cA, cB)) continue;

    const s = (dot2(seg.start, u) + dot2(seg.end, u)) / 2;
    const gap = s - s0;
    if (gap < 0) {
      if (!below || gap > below.gap) below = { id, gap };
    } else if (gap > 0) {
      if (!above || gap < above.gap) above = { id, gap };
    }
  }

  if (!below || !above) return null;
  return [
    { kind: 'wall', id: below.id },
    { kind: 'wall', id: above.id },
  ];
}

/**
 * The axes a subject can be centred on in this room, with the pair each would
 * use. PURE. Used by the authoring UI to offer one click where the answer is
 * unambiguous, and to say nothing where it is not.
 *
 * @returns {{axis:'x'|'z', datum:[Object,Object]}[]}
 */
function availableCentringAxes(wallSegments, centre) {
  const out = [];
  for (const [axis, u] of Object.entries(AXES)) {
    const datum = nearestFacingPair(wallSegments, centre, u);
    if (datum) out.push({ axis, datum });
  }
  return out;
}

module.exports = { nearestFacingPair, availableCentringAxes, perp };
